// The one screen header: a round back button, a serif title (optionally under an eyebrow) and
// trailing actions. One component keeps the hit target, the label and the look identical.
import "./config";
import { useT } from "../lib/i18n";
import { back } from "../lib/router";
import { IconButton } from "./controls";
import { Icon } from "./Icon";
import { Column, Fill, Row } from "./layout";
import { Eyebrow, Title } from "./text";

export interface ScreenHeaderProps {
  title?: string | undefined;
  eyebrow?: string | undefined;
  /** Where `back()` lands when there is no history to pop; omit both this and `onBack` for a
   * top-level tab screen without a back button. */
  backFallback?: string | undefined;
  onBack?: (() => void) | undefined;
  trailing?: React.ReactNode;
  /** Replaces the title block (the note wizard puts its progress bar here). */
  children?: React.ReactNode;
  /** Leaves the header off the printed page. */
  printHidden?: boolean;
}

export function ScreenHeader({
  title,
  eyebrow,
  backFallback,
  onBack,
  trailing,
  children,
  printHidden = false,
}: ScreenHeaderProps): React.JSX.Element {
  const { t } = useT();
  const showBack = onBack !== undefined || backFallback !== undefined;
  return (
    <Row
      render="header"
      gap="3"
      minHeight={44}
      // nosemgrep: rules.typescript.react.best-practice.react-props-spreading -- props are typed as the component's own
      {...(printHidden ? { className: "ui-no-print" } : {})}
    >
      {showBack && (
        <IconButton
          aria-label={t("common.nav.back")}
          data-testid="screen-back"
          onPress={() => {
            if (onBack !== undefined) onBack();
            else back(backFallback ?? "/");
          }}
        >
          <Icon name="chevron-left" />
        </IconButton>
      )}
      {children ?? (
        <Fill>
          <Column gap={2}>
            {eyebrow === undefined ? null : <Eyebrow color="accent">{eyebrow}</Eyebrow>}
            {title === undefined ? null : (
              <Title render="h1" overflowWrap="anywhere">
                {title}
              </Title>
            )}
          </Column>
        </Fill>
      )}
      {trailing === undefined ? null : (
        <Row gap="2" flexShrink={0}>
          {trailing}
        </Row>
      )}
    </Row>
  );
}
